import type Mdast from "mdast";
import * as op from "@/shared/operation";
import { markdownToTree } from "./tree";

export const repairMarkdown = (content: string) => {
  try {
    // Repair non-closed bold markdown syntax (**)
    const boldMatches = content.match(/\*\*/g);
    if (boldMatches && boldMatches.length % 2 !== 0) return op.success(`${content}**`);
    return op.success(content);
  } catch (error) {
    return op.failure({ code: "Unknown", cause: error });
  }
};

/*
 * Repair a Markdown tree in place.
 */
export const repairTree = (parent: Mdast.Nodes) => {
  try {
    // Return early if the parent has no children or can't contain Markdown
    const hasChildren = "children" in parent && parent.children?.length;
    const canContainMarkdown = !["code", "inlineCode", "math"].includes(parent.type);
    if (!(hasChildren && canContainMarkdown)) return op.success();

    for (let nodeIndex = 0; nodeIndex < parent.children.length; nodeIndex++) {
      const node = parent.children.at(nodeIndex);
      if (!node) continue;

      // Process text nodes to find and recover partial or broken Markdown sequences
      if (node.type === "text") {
        const text = node.value;
        const [errRepaired, repaired] = repairMarkdown(text);
        if (errRepaired) continue;

        // If the node had partial or broken Markdown
        if (repaired !== text) {
          // Replace the original tree node, by a new tree with the repaired Markdown
          const [errRepairedTree, repairedTree] = markdownToTree(repaired);
          if (errRepairedTree) return op.failure(errRepairedTree);
          const newChildren = repairedTree.children.flatMap((child) =>
            child.type === "paragraph" ? child.children : [child],
          );
          parent.children = [
            ...parent.children.slice(0, nodeIndex),
            ...newChildren,
            ...parent.children.slice(nodeIndex + 1),
          ];

          // Adjust nodeIndex to account for the replacement, to skip the new nodes
          nodeIndex += repairedTree.children.length - 1;
        }
      }

      // Repair children recursively
      const [errRepairChildren] = repairTree(node) as op.OperationResult<void>;
      if (errRepairChildren) return op.failure(errRepairChildren);
    }
    // Return success
    return op.success();
  } catch (error) {
    return op.failure({ code: "Unknown", cause: error });
  }
};
